import {
  type FilterParams,
  type TransformedFilter,
  type Filter,
  isFilterActive,
  transformToWhereCondition,
  serializeFilters,
  deserializeFilters,
  type SortingValueKey,
  getSortByValue,
} from '@scayle/storefront-core'
import { computed } from 'vue'
import { useRoute, useRouter } from '#app/composables/router'
import type {
  LocationQuery,
  LocationQueryRaw,
  LocationQueryValue,
} from '#vue-router'

const DEFAULT_QUERY_KEYS = ['page', 'sort', 'term']
type Options = {
  defaultSort?: SortingValueKey
}

type FilterValue = string | number | boolean | null
export type ActiveFilters = Record<string, FilterValue | FilterValue[]>

function queryValueToString(
  value: LocationQueryValue | LocationQueryValue[],
): string | undefined {
  return Array.isArray(value)
    ? value
        .map((v) => v?.toString())
        .filter(Boolean)
        .join(',')
    : value?.toString()
}

const isEqual = <T>(x: T, y: T): boolean => {
  if (Object.is(x, y)) {
    return true
  }
  // For non-objects or null, use strict equality
  if (
    typeof x !== 'object' ||
    x === null ||
    typeof y !== 'object' ||
    y === null
  ) {
    return x === y
  }
  // Compare objects
  const keys = Object.keys(x) as (keyof T)[]
  return (
    keys.length === Object.keys(y).length &&
    keys.every((key) => Reflect.has(y, key) && isEqual(x[key], y[key]))
  )
}
const getCurrentPage = (query: LocationQuery): number | undefined => {
  return typeof query.page === 'string' ? parseInt(query.page, 10) : undefined
}

export function useQueryFilterState(options: Options = {}) {
  const router = useRouter()
  const route = useRoute()
  const activeFilters = computed(() => {
    const allowedFilters = Object.fromEntries(
      Object.entries(route.query)
        .filter(([key]) => !DEFAULT_QUERY_KEYS.includes(key))
        .map(([key, value]) => [key, queryValueToString(value)])
        .filter(([_key, value]) => !!value),
    )
    return deserializeFilters(allowedFilters) as ActiveFilters
  })
  const isActiveFilter = (filter?: TransformedFilter) => {
    if (!filter) {
      return Object.values(activeFilters.value).length === 0
    }
    return isFilterActive(activeFilters.value, filter)
  }
  const applyFilters = async (filter?: Filter, scrollToTop = true) => {
    const newQuery: LocationQueryRaw = {
      sort: route.query.sort,
      term: route.query.term,
      ...(filter ? serializeFilters(filter) : {}),
    }
    if (isEqual<LocationQueryRaw>(route.query, newQuery)) {
      return
    }
    await router.push({
      query: { ...newQuery },
    })
    if (scrollToTop) {
      window.scroll({
        behavior: 'smooth',
        top: 0,
      })
    }
  }
  const resetFilterUrl = async () => {
    await router.replace({ query: { term: route.query.term } })
  }
  const generateFilterParams = (): FilterParams => {
    return {
      where: { ...transformToWhereCondition(activeFilters.value) },
      page: getCurrentPage(route.query),
      sort: getSortByValue(route.query?.sort || '', options?.defaultSort),
    }
  }
  return {
    activeFilters,
    applyFilters,
    isActiveFilter,
    resetFilterUrl,
    productConditions: computed(() => generateFilterParams()),
  }
}
